import { Item } from '../types/interfaces';

// ─────────────────────────────────────────────────────────────────────────────
// Types
// ─────────────────────────────────────────────────────────────────────────────

export interface ForgeRecipeIngredient {
  item: Item;
  quantity: number;
}

export interface ForgeRecipe extends Item {
  forge_recipe_details: ForgeRecipeIngredient[];
}

export interface ForgeQueueEntry {
  id: string;
  character_id: string;
  item_id: string;
  consumed_materials: Array<{ item_id: string; quantity: number }>;
  consumed_kamas: number;
  status: 'in_progress' | 'ready' | 'collected';
  started_at: string;
  finishes_at: string | null;
  collected_at: string | null;
  item?: Item;
}

// ─────────────────────────────────────────────────────────────────────────────
// API Functions
// ─────────────────────────────────────────────────────────────────────────────

/**
 * Lista todas as receitas de forja disponíveis (itens com requires_forge = true).
 */
export async function fetchForgeRecipes(token: string): Promise<ForgeRecipe[]> {
  const res = await fetch('/api/user/forge/recipes', {
    headers: { Authorization: `Bearer ${token}` },
  });
  if (!res.ok) throw new Error('Falha ao carregar receitas de forja');
  return res.json();
}

/**
 * Inicia o processo de forja de um item.
 */
export async function startForge(
  token: string,
  itemId: string
): Promise<{ status: string; message: string; queue?: ForgeQueueEntry; item?: Item }> {
  const res = await fetch('/api/user/forge/start', {
    method: 'POST',
    headers: {
      Authorization: `Bearer ${token}`,
      'Content-Type': 'application/json',
    },
    body: JSON.stringify({ item_id: itemId }),
  });
  const data = await res.json();
  if (!res.ok) throw new Error(data.error || 'Falha ao iniciar forja');
  return data;
}

/**
 * Retorna a fila de forja do jogador (em progresso + prontos).
 */
export async function fetchForgeQueue(
  token: string
): Promise<{ queue: ForgeQueueEntry[] }> {
  const res = await fetch('/api/user/forge/queue', {
    headers: { Authorization: `Bearer ${token}` },
  });
  if (!res.ok) throw new Error('Falha ao carregar fila de forja');
  return res.json();
}

/**
 * Coleta um item cuja forja foi concluída.
 */
export async function collectForgeItem(
  token: string,
  queueId: string
): Promise<{ message: string; item: Item }> {
  const res = await fetch(`/api/user/forge/collect/${queueId}`, {
    method: 'POST',
    headers: { Authorization: `Bearer ${token}` },
  });
  const data = await res.json();
  if (!res.ok) throw new Error(data.error || 'Falha ao coletar item');
  return data;
}
